import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { motion } from 'framer-motion';
import { Play, Pause, Music2, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDuration } from '@/lib/audioUtils';
import { TrackMenu, LikeButton } from '@/components/TrackMenu';
import type { MusicTrack } from '@/hooks/useMusicTracks';

interface TrackTileProps {
  track: MusicTrack;
  onPlay: () => void;
  isPlaying?: boolean;
  isSelected?: boolean;
  showArtist?: boolean;
  index?: number;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

export function TrackTile({ 
  track, 
  onPlay, 
  isPlaying, 
  isSelected,
  showArtist = true,
  index = 0,
  className,
  size = 'md'
}: TrackTileProps) {
  const npub = nip19.npubEncode(track.event.pubkey);
  
  const sizeClasses = {
    sm: 'w-36 sm:w-40',
    md: 'w-40 sm:w-44',
    lg: 'w-48 sm:w-56',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ 
        duration: 0.4, 
        delay: index * 0.05,
        ease: [0.16, 1, 0.3, 1]
      }}
      className={cn(
        'group flex-shrink-0 cursor-pointer track-tile',
        'snap-start', // Snap to start for smooth mobile scrolling
        sizeClasses[size],
        className
      )}
      onClick={onPlay}
    >
      {/* Cover Art */}
      <div className={cn(
        'relative aspect-square rounded-2xl overflow-hidden mb-3',
        'bg-gradient-to-br from-white/5 to-white/[0.02]',
        'backdrop-blur-sm border border-white/10',
        'shadow-lg transition-all duration-300',
        'group-hover:shadow-xl group-hover:border-white/20',
        'group-hover:scale-[1.02]',
        isSelected && 'ring-2 ring-primary ring-offset-2 ring-offset-background',
        isPlaying && 'animate-glow-pulse'
      )}>
        {track.coverImage ? (
          <img
            src={track.coverImage}
            alt={track.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-white/5 to-transparent">
            <Music2 className="h-12 w-12 text-white/20" />
          </div>
        )}

        {/* Gradient overlay on hover */}
        <div className={cn(
          'absolute inset-0',
          'bg-gradient-to-t from-black/80 via-black/30 to-transparent',
          'opacity-0 group-hover:opacity-100 transition-opacity duration-300'
        )} />

        {/* Top right actions - visible on hover */}
        <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <LikeButton track={track} className="bg-black/40 backdrop-blur-sm" />
          <TrackMenu track={track} className="bg-black/40 backdrop-blur-sm" />
        </div>

        {/* Play Button */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          className={cn(
            'absolute inset-0 flex items-center justify-center',
            'opacity-0 group-hover:opacity-100 transition-all duration-300',
            isPlaying && 'opacity-100'
          )}
        >
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            className={cn(
              'h-12 w-12 rounded-full flex items-center justify-center',
              'bg-white text-black shadow-2xl',
              'hover:bg-white/90 transition-colors',
              !isPlaying && 'translate-y-2 group-hover:translate-y-0 transition-transform duration-300'
            )}
            onClick={(e) => {
              e.stopPropagation();
              onPlay();
            }}
          >
            {isPlaying ? (
              <Pause className="h-5 w-5" fill="currentColor" />
            ) : (
              <Play className="h-5 w-5 ml-0.5" fill="currentColor" />
            )}
          </motion.button>
        </motion.div>

        {/* Duration badge */}
        {track.duration > 0 && (
          <div className={cn(
            'absolute bottom-2 right-2 px-2 py-0.5 rounded-md text-xs font-medium',
            'bg-black/60 text-white/90 backdrop-blur-sm',
            'opacity-0 group-hover:opacity-100 transition-opacity duration-300'
          )}>
            {formatDuration(track.duration)}
          </div>
        )}

        {/* Playing indicator */}
        {isPlaying && (
          <div className="absolute bottom-2 left-2 flex items-end gap-0.5 h-4">
            {[0, 1, 2, 3].map((i) => (
              <motion.div
                key={i}
                animate={{ height: ['40%', '100%', '40%'] }}
                transition={{ 
                  repeat: Infinity, 
                  duration: 0.8, 
                  delay: i * 0.1,
                  ease: 'easeInOut'
                }}
                className="w-1 bg-primary rounded-full"
              />
            ))}
          </div>
        )}

        {/* AI Generated badge */}
        {track.isAiGenerated && (
          <div className="absolute top-2 left-2 px-2 py-1 rounded-full bg-purple-500/80 backdrop-blur-sm flex items-center gap-1">
            <Sparkles className="h-3 w-3 text-white" />
            <span className="text-[10px] font-medium text-white">AI</span>
          </div>
        )}
      </div>

      {/* Track Info */}
      <div className="space-y-0.5 px-0.5">
        <h3 className={cn(
          'font-medium text-sm line-clamp-1 transition-colors',
          isSelected ? 'text-primary' : 'group-hover:text-white'
        )}>
          {track.title}
        </h3>
        {showArtist && (
          <Link 
            to={`/${npub}`}
            onClick={(e) => e.stopPropagation()}
            className="block"
          >
            <p className="text-xs text-muted-foreground line-clamp-1 hover:text-foreground hover:underline transition-colors">
              {track.artist}
            </p>
          </Link>
        )}
      </div>
    </motion.div>
  );
}
